import type { FC, ReactNode } from 'react'
import type { CourseDetail } from '@/types'

import { Check, Users } from 'lucide-react'
import { enrollHref } from '@/lib/routes'
import { ButtonLink } from '@/components/generals/button-link'
import { CmsImage } from '@/components/generals/cms-image'

interface CourseBodyProps {
    course: CourseDetail
}

interface BlockHeadingProps {
    id: string
    eyebrow: string
    children: ReactNode
}

const BlockHeading: FC<BlockHeadingProps> = ({ id, eyebrow, children }): ReactNode => {
    return (
        <div>
            <p className='inline-flex items-center gap-2 font-mono text-xs font-medium tracking-wider text-primary uppercase'>
                <span aria-hidden='true'>{'//'}</span>
                {eyebrow}
            </p>

            <h2 id={id} className='mt-2 text-2xl font-semibold sm:text-3xl'>
                {children}
            </h2>
        </div>
    )
}

export const CourseBody: FC<CourseBodyProps> = ({ course }): ReactNode => {
    const { instructor, modules } = course

    return (
        <div className='min-w-0 space-y-16'>
            {course.description && (
                <section id='about' aria-labelledby='about-title'>
                    <BlockHeading id='about-title' eyebrow="Overview">
                        About this course
                    </BlockHeading>

                    <p className='mt-5 text-base leading-relaxed whitespace-pre-line text-muted-foreground sm:text-lg' data-mdlfy-cms={`courses:${course.id}:description`}>
                        {course.description}
                    </p>
                </section>
            )}

            {course.outcomes.length > 0 && (
                <section id='outcomes' aria-labelledby='outcomes-title'>
                    <BlockHeading id='outcomes-title' eyebrow="Outcomes">
                        What you&apos;ll learn
                    </BlockHeading>

                    <ul className='mt-6 grid gap-3 sm:grid-cols-2'>
                        {course.outcomes.map((outcome, index) => (
                            <li key={`${index}-${outcome}`} className='flex items-start gap-3 rounded-2xl border bg-card p-4'>
                                <span className='mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground'>
                                    <Check className='size-3.5' aria-hidden='true' />
                                </span>

                                <span className='text-sm leading-relaxed sm:text-base'>
                                    {outcome}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section id='curriculum' aria-labelledby='curriculum-title'>
                <div className='flex flex-wrap items-end justify-between gap-3'>
                    <BlockHeading id='curriculum-title' eyebrow="Curriculum">
                        Course curriculum
                    </BlockHeading>

                    {modules.length > 0 && (
                        <p className='font-mono text-xs text-muted-foreground'>
                            {modules.length} {modules.length === 1 ? "module" : "modules"}
                        </p>
                    )}
                </div>

                {modules.length > 0
                    ? (
                        <ol className='mt-6 space-y-3'>
                            {modules.map((lesson, index) => (
                                <li key={lesson.id} className='flex gap-4 rounded-2xl border bg-card p-5 sm:gap-5 sm:p-6'>
                                    <span aria-hidden='true' className='flex size-11 shrink-0 items-center justify-center rounded-xl bg-secondary font-mono text-sm font-semibold text-primary'>
                                        {String(index + 1).padStart(2, '0')}
                                    </span>

                                    <div className='min-w-0'>
                                        <h3 className='text-lg leading-snug font-semibold' data-mdlfy-cms={`course_modules:${lesson.id}:title`}>
                                            {lesson.title}
                                        </h3>

                                        {lesson.description && (
                                            <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground' data-mdlfy-cms={`course_modules:${lesson.id}:description`}>
                                                {lesson.description}
                                            </p>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    )
                    : (
                        <div className='mt-6 flex flex-col items-start gap-4 rounded-2xl border border-dashed bg-card p-6 sm:flex-row sm:items-center sm:justify-between'>
                            <p className='text-muted-foreground'>
                                The detailed lesson plan is being finalised. Ask an advisor and we will send it to you.
                            </p>

                            <ButtonLink href={enrollHref(course.slug)} variant='outline' size='sm'>
                                Ask an advisor
                            </ButtonLink>
                        </div>
                    )}
            </section>

            {course.audience && (
                <section id='audience' aria-labelledby='audience-title'>
                    <BlockHeading id='audience-title' eyebrow="Is it for you?">
                        Who this course is for
                    </BlockHeading>

                    <div className='mt-6 flex gap-4 rounded-2xl border bg-secondary/50 p-5 sm:p-6'>
                        <span className='flex size-11 shrink-0 items-center justify-center rounded-xl bg-card text-primary'>
                            <Users className='size-5' aria-hidden='true' />
                        </span>

                        <p className='text-base leading-relaxed sm:text-lg' data-mdlfy-cms={`courses:${course.id}:audience`}>
                            {course.audience}
                        </p>
                    </div>
                </section>
            )}

            {instructor && (
                <section id='instructor' aria-labelledby='instructor-title'>
                    <BlockHeading id='instructor-title' eyebrow="Instructor">
                        Your instructor
                    </BlockHeading>

                    <div className='mt-6 flex flex-col gap-6 rounded-3xl border bg-card p-5 sm:flex-row sm:p-6'>
                        <div className='relative aspect-[4/3] w-full shrink-0 overflow-hidden rounded-2xl bg-secondary sm:aspect-square sm:w-40'>
                            <CmsImage
                                src={instructor.photo}
                                alt={instructor.photo_alt}
                                sizes='(min-width: 640px) 160px, 100vw'
                                className='object-[center_25%]'
                            />
                        </div>

                        <div className='min-w-0'>
                            <h3 className='text-xl font-semibold' data-mdlfy-cms={`instructors:${instructor.id}:name`}>
                                {instructor.name}
                            </h3>

                            <p className='mt-1 text-sm font-medium text-primary' data-mdlfy-cms={`instructors:${instructor.id}:role`}>
                                {instructor.role}
                            </p>

                            {instructor.bio && (
                                <p className='mt-3 leading-relaxed text-muted-foreground' data-mdlfy-cms={`instructors:${instructor.id}:bio`}>
                                    {instructor.bio}
                                </p>
                            )}
                        </div>
                    </div>
                </section>
            )}
        </div>
    )
}
